import { Link } from "@tanstack/react-router";

import { EmptyCard } from "../../components/empty-card";
import { PriceLines, priceLineOf } from "../../components/price-lines";
import { Section, SectionHead, ghostButton, smallButton } from "../../components/section";
import { PageTitle, SiteHead } from "../../components/site-head";
import { SitePage } from "../../components/site-page";
import { usePublicDirectory } from "../../hooks/use-public-directory";
import { sitePath, useSiteConfig } from "../../hooks/use-site-config";
import { Skeleton } from "@/components/ui/skeleton";
import { TrainerOffers, type Offer } from "@/features/payments/modules/trainer-offers/trainer-offers";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The trainer's prices: every booking service with its length and price, as Setup has them. The
 * line under the title is the trainer's own payment note, when they wrote one.
 */
export const Pricing = ({ offers }: { offers?: Offer[] }) => {
  const site = useSiteConfig();
  const { directory, loading } = usePublicDirectory({ days: site.calendarDays });
  const services = directory?.services ?? [];

  return (
    <SitePage
      head={
        <SiteHead>
          <PageTitle title={m.landing_nav_pricing()} aside={directory?.location.paymentNote || undefined} />
        </SiteHead>
      }
    >
      {loading ? (
        <Skeleton className="mt-3 h-64 rounded-[28px]" />
      ) : directory && services.length > 0 ? (
        <Section className="pt-3 md:pt-3">
          <SectionHead
            title={
              <>
                {m.pricing_list_title_lead()}
                <br />
                {m.pricing_list_title_tail()}
              </>
            }
          />
          <PriceLines
            lines={services.map((service) => priceLineOf(service, directory.business.currency, true))}
            action={() => (
              <Link to={sitePath(site.slug)} hash="times" className={cn(ghostButton, smallButton)}>
                {m.pricing_book()}
              </Link>
            )}
          />
        </Section>
      ) : (
        <EmptyCard title={m.services_empty_title()} />
      )}
      {site.organizationId ? (
        <Section className="pt-3 md:pt-3">
          <TrainerOffers organizationId={site.organizationId} initial={offers} />
        </Section>
      ) : null}
    </SitePage>
  );
};
